import {
  ComputablePricing,
  PartialCompleteBuyableItem,
} from "@ac-expo/api-types";
import {
  centsToPrice,
  computeMultipleItemsPrice,
  DiscountCodeToApply,
  getBuyableItemGroupKey,
} from "@ac-expo/utils";
import { sortBy, uniqBy } from "lodash";
import { useCallback, useMemo, useState } from "react";

export type BuyableItemWithPricing = {
  buyableItem: PartialCompleteBuyableItem;
  pricing: ComputablePricing;
};

export type LocalCartItem = {
  buyableItem: PartialCompleteBuyableItem;
  pricing: ComputablePricing;
  key: string;
  quantity: number;
};

const calculateLocalCartPricing = (
  items: LocalCartItem[],
  appliedDiscountCodes: DiscountCodeToApply[] = []
) => {
  const itemsWithQuantity = items.filter((item) => item.quantity > 0);

  if (itemsWithQuantity.length === 0) {
    return {
      total: 0,
      exclTaxesTotal: 0,
      discount: 0,
      discountExclTaxes: 0,
    };
  }

  const priceComputation = computeMultipleItemsPrice(
    itemsWithQuantity.map((item) => ({
      pricing: item.pricing,
      quantity: item.quantity,
      appliedDiscountCodes,
    }))
  );

  return {
    items: priceComputation.items,
    total: centsToPrice(priceComputation.finalPrice),
    exclTaxesTotal: centsToPrice(priceComputation.finalExclTaxesPrice),
    discount: centsToPrice(
      priceComputation.degressiveDiscountAmount +
        priceComputation.codeDiscountAmount
    ),
    degressiveDiscount: centsToPrice(priceComputation.degressiveDiscountAmount),
    codeDiscount: centsToPrice(priceComputation.codeDiscountAmount),
    discountExclTaxes: centsToPrice(
      priceComputation.degressiveDiscountAmountExclTaxes +
        priceComputation.codeDiscountAmountExclTaxes
    ),
  };
};

export const useLocalCart = (
  items: BuyableItemWithPricing[],
  baseQuantities: Record<string, number> = {},
  appliedDiscountCodes: DiscountCodeToApply[] = []
) => {
  const [localQuantities, setLocalQuantities] = useState<
    Record<string, number>
  >({});

  const totalQuantities = useMemo(() => {
    const result: Record<string, number> = {};

    items.forEach((item) => {
      const key = getBuyableItemGroupKey(item.buyableItem);
      const localQty = localQuantities[key] || 0;
      const baseQty = baseQuantities[key] || 0;
      result[key] = localQty + baseQty;
    });

    return result;
  }, [items, localQuantities, baseQuantities]);

  const cartItems = useMemo(
    (): LocalCartItem[] =>
      sortBy(
        uniqBy(
          items.map((item) => {
            const key = getBuyableItemGroupKey(item.buyableItem);
            return {
              ...item,
              key,
              quantity: totalQuantities[key] || 0,
            };
          }),
          "key"
        ),
        (a) => a.key
      ),
    [items, totalQuantities]
  );

  // Same key order as cartItems: a capped code is allocated to the same units in every list,
  // so total − base stays meaningful.
  const baseCartItems = useMemo(
    () =>
      sortBy(
        uniqBy(
          items.map((item) => {
            const key = getBuyableItemGroupKey(item.buyableItem);
            return {
              ...item,
              key,
              quantity: baseQuantities[key] || 0,
            };
          }),
          (a) => a.key
        ),
        (a) => a.key
      ),
    [items, baseQuantities]
  );

  const localCartItems = useMemo(
    () =>
      sortBy(
        uniqBy(
          items.map((item) => {
            const key = getBuyableItemGroupKey(item.buyableItem);
            return {
              ...item,
              key,
              quantity: localQuantities[key] || 0,
            };
          }),
          (a) => a.key
        ),
        (a) => a.key
      ),
    [items, localQuantities]
  );

  const totalPricing = useMemo(
    () => calculateLocalCartPricing(cartItems, appliedDiscountCodes),
    [cartItems, appliedDiscountCodes]
  );

  const basePricing = useMemo(
    () => calculateLocalCartPricing(baseCartItems, appliedDiscountCodes),
    [baseCartItems, appliedDiscountCodes]
  );

  const localPricing = useMemo(
    () => ({
      total: totalPricing.total - basePricing.total,
      exclTaxesTotal: totalPricing.exclTaxesTotal - basePricing.exclTaxesTotal,
      discount: totalPricing.discount - basePricing.discount,
      discountExclTaxes:
        totalPricing.discountExclTaxes - basePricing.discountExclTaxes,
    }),
    [totalPricing, basePricing]
  );

  const setQuantity = useCallback(
    (buyableItemKey: string, quantity: number) => {
      setLocalQuantities((prev) => ({
        ...prev,
        [buyableItemKey]: quantity,
      }));
    },
    []
  );

  const incrementItem = useCallback((buyableItemKey: string, quantity = 1) => {
    setLocalQuantities((prev) => ({
      ...prev,
      [buyableItemKey]: (prev[buyableItemKey] || 0) + quantity,
    }));
  }, []);

  const decrementItem = useCallback((buyableItemKey: string, quantity = 1) => {
    setLocalQuantities((prev) => ({
      ...prev,
      [buyableItemKey]: (prev[buyableItemKey] || 0) - quantity,
    }));
  }, []);

  const decrementItems = useCallback((quantities: Record<string, number>) => {
    setLocalQuantities((prev) => {
      const newQuantities = Object.entries(prev).map(([key, value]) => {
        return [key, value - (quantities[key] || 0)];
      });
      return Object.fromEntries(newQuantities);
    });
  }, []);

  const getBaseQuantity = useCallback(
    (buyableItemKey: string) => {
      return baseQuantities[buyableItemKey] || 0;
    },
    [baseQuantities]
  );

  const removeItem = useCallback(
    (buyableItemKey: string) => {
      const newQuantity = -getBaseQuantity(buyableItemKey);
      setQuantity(buyableItemKey, newQuantity);
    },
    [setQuantity, getBaseQuantity]
  );

  const resetCart = useCallback(() => {
    setLocalQuantities({});
  }, []);

  const getLocalQuantity = useCallback(
    (buyableItemKey: string) => localQuantities[buyableItemKey] || 0,
    [localQuantities]
  );

  const getTotalQuantity = useCallback(
    (buyableItemKey: string) => totalQuantities[buyableItemKey] || 0,
    [totalQuantities]
  );

  const getItemFinalPricing = useCallback(
    (buyableItemKey: string) => {
      const buyableItemPricing = cartItems.find(
        (item) => item.key === buyableItemKey
      )?.pricing;
      return totalPricing.items?.find(
        (item) => item.pricingId === buyableItemPricing?.id
      );
    },
    [cartItems, totalPricing.items]
  );

  return {
    cartItems,
    baseCartItems,
    localCartItems,
    totalPricing,
    basePricing,
    localPricing,
    localQuantities,
    baseQuantities,
    totalQuantities,
    setQuantity,
    incrementItem,
    decrementItem,
    decrementItems,
    removeItem,
    resetCart,
    getLocalQuantity,
    getBaseQuantity,
    getTotalQuantity,
    getItemFinalPricing,
  };
};

export type UseLocalCartReturns = ReturnType<typeof useLocalCart>;
